import {
  runRenameCanvasWorkflow,
  runRenameChatWorkflow,
  runShareCanvasWorkflow,
} from "@/action-dialog";
import { useCanvasById } from "@/api/canvas";
import { useFlowSessionById } from "@/api/flow_sessions";
import "@/components/rail-kit/rail-kit.css";
import { Accordion } from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes";
import { CanvasRailBody } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/canvas/CanvasRail";
import {
  FilesRailCanvasFilesList,
  FilesRailChatContextBody,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailContextSection";
import { consumeTabBarContainerNavigation } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/railDrawerNavigationSource";
import { RailGroup } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroup";
import { ChatRowDropZone } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRailChatNode";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import {
  ChevronRight,
  FileText,
  Frame,
  MessagesSquare,
  MoreHorizontal,
  PanelBottomClose,
  PenLine,
  Share2,
  SquareArrowOutUpRight,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";

/**
 * The rail drawer — the active container's contents docked into the rail's
 * bottom, above the Archived link and footer (design-integration Phase 4;
 * supersedes D21's keep-the-contextual-tab clause). One component, two
 * scopes: an open CANVAS shows chats + canvas contents (CanvasRailBody); an
 * open CHAT shows its files. State machine, driven by the route:
 *
 *   none ──enter container──▶ active (half the space under the search, resizable)
 *   active ──leave to Home/workspace──▶ docked (header row, contents cached)
 *   docked ──header press──▶ re-expanded in place (peek without entering)
 *   docked ──⤢──▶ re-enters the container's page
 *   docked ──⋯ → Close──▶ dismissed (cache survives for the next enter)
 *
 * Structural motion rides the house deceleration curve — the drawer settles
 * into place rather than springing past it; hovers stay on the standard
 * register. Collapses to instant swaps under prefers-reduced-motion.
 */

// Shares of the region between the search row and the footer — not of the
// whole card. 100 takes the drawer as high as it goes.
const PCT_DEFAULT = 40;
const PCT_MIN = 25;
const PCT_MAX = 100;

/** The drawer's own header rung, which sits above the measured body. */
const HEADER_PX = 48;
/** Air kept between the fully-expanded drawer and the search row above it. */
const TOP_INSET_PX = 8;
/** Air kept between the active rail row and the auto-opened drawer's edge. */
const ACTIVE_ROW_GAP_PX = 8;
const clampPct = (v: number) => Math.min(PCT_MAX, Math.max(PCT_MIN, v));

/**
 * The largest share the drawer may auto-open to without pushing the
 * route-active rail row (the canvas/chat the user just clicked) below the
 * fold. The drawer is a flex sibling: expanding it shrinks the list above,
 * so "covered" means the row leaves the visible scroll window. Null when
 * nothing constrains — no active row rendered, or it is already out of view
 * (a taller drawer changes nothing the user can currently see).
 */
function measureAutoOpenCap(section: HTMLElement | null): number | null {
  const host = section?.parentElement;
  if (!host) return null;
  const row = host.querySelector('[aria-current="page"]');
  if (!(row instanceof HTMLElement)) return null;
  const hostRect = host.getBoundingClientRect();
  const rowRect = row.getBoundingClientRect();
  if (hostRect.height <= 0) return null;
  // Already above the window or below the current fold — unconstrainable.
  if (rowRect.top < hostRect.top || rowRect.bottom > hostRect.bottom) {
    return null;
  }
  const room = hostRect.bottom - rowRect.bottom - ACTIVE_ROW_GAP_PX;
  return (room / hostRect.height) * 100;
}

type DrawerScope = { kind: "canvas" | "chat"; id: string };

/**
 * The section chrome every files list in the drawer sits under — the same
 * Accordion + RailGroup rung the canvas body uses for its own sections, so
 * both drawer scopes (and the docked canvas peek) read as one design.
 */
function DrawerFilesGroup({
  label,
  children,
}: {
  /** Scope-explicit ("Chat Files" / "Canvas Files") so the section reads
      unambiguously next to the canvas body's Canvas Chats/Contents. */
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Accordion
      type="multiple"
      defaultValue={["drawer-files"]}
      className="flex flex-col gap-1"
    >
      <RailGroup
        groupId="drawer-files"
        label={label}
        icon={FileText}
        noCap
        items={[
          <React.Fragment key="drawer-files-body">{children}</React.Fragment>,
        ]}
      />
    </Accordion>
  );
}

export function RailDrawer() {
  const canvasMatch = useMatch(ROUTES.CANVAS.DETAIL);
  const chatMatch = useMatch(ROUTES.CHAT.DETAIL);
  const canvasId = canvasMatch?.params?.canvasId ?? null;
  const flowSessionId = chatMatch?.params?.flowSessionId ?? null;
  const navigate = useNavigate();

  const active: DrawerScope | null = canvasId
    ? { kind: "canvas", id: canvasId }
    : flowSessionId
      ? { kind: "chat", id: flowSessionId }
      : null;

  // The cached scope — survives leaving the page (docked) and dismissal.
  const [scope, setScope] = useState<DrawerScope | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // The drawer's remembered share. Manual resizes write it, and a rail-driven
  // auto-open that had to shrink below it to keep the clicked row visible
  // COMMITS the shrunken value here too (Aug 2026 ruling) — otherwise the
  // next tab-bar switch, which skips the collision cap, would jump back to
  // the old size.
  const [pct, setPct] = useState(PCT_DEFAULT);
  const [resizing, setResizing] = useState(false);
  // A HAND-collapse (header press) is a preference, same as a hand-resize
  // (Aug 2026 ruling): the drawer stays collapsed across container entries
  // until the user expands it by hand. Only the header gesture writes this —
  // the route-driven dock when leaving to Home/workspace is automatic and
  // exempt. A ref, not state: the auto-open effect reads it without
  // re-running on header presses.
  const manuallyCollapsedRef = useRef(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!active) {
      setExpanded(false);
      return;
    }
    setScope((prev) =>
      prev?.kind === active.kind && prev.id === active.id ? prev : active,
    );
    setDismissed(false);
    // The collision cap protects a rail selection. A switch made through the
    // tab bar has none, so it opens at the normal size (stored share or
    // default) — consume the mark before the measuring frames below.
    const fromTabBar = consumeTabBarContainerNavigation();
    // Open on the second frame, not this one. Entering a container mounts it
    // — a canvas board, its realtime connection — and that work blocks paint
    // for over 100ms. A transition started inside that window still advances
    // on wall-clock while nothing renders, so the first frame the user
    // actually sees is already most of the way through: the drawer appears to
    // snap. Waiting for a painted frame costs a few milliseconds and buys the
    // whole animation.
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        // Collapsed by hand — entering another container respects that and
        // leaves the drawer as a header row.
        if (manuallyCollapsedRef.current) return;
        // Measured on the painted frame right before expansion, while the
        // rows still hold their pre-drawer positions. A cap that constrains
        // becomes the stored share (PCT_MIN still floors it — below that the
        // drawer stops being usable, so a row in the rail's bottom quarter
        // concedes instead).
        const cap = fromTabBar ? null : measureAutoOpenCap(sectionRef.current);
        if (cap !== null) {
          setPct((prev) => Math.max(PCT_MIN, Math.min(prev, cap)));
        }
        setExpanded(true);
      });
    });
    return () => {
      cancelAnimationFrame(first);
      if (second) cancelAnimationFrame(second);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.kind, active?.id]);

  const { data: scopeCanvas } = useCanvasById(
    scope?.kind === "canvas" ? scope.id : "",
  );
  const { data: scopeSession } = useFlowSessionById(
    scope?.kind === "chat" ? scope.id : "",
  );

  const present = !!scope && !dismissed;

  // Keep the body mounted through the close animation, then drop it. The
  // section itself must stay rendered for that animation to run, but its
  // contents must not: a dismissed drawer still holds a scope, and leaving
  // its subtree alive would keep the canvas's chats, contents and their
  // queries working inside a zero-height box nobody can see.
  const [bodyMounted, setBodyMounted] = useState(present);
  useEffect(() => {
    if (present) {
      setBodyMounted(true);
      return;
    }
    const timer = window.setTimeout(() => setBodyMounted(false), 300);
    return () => window.clearTimeout(timer);
  }, [present]);
  const docked = present && !active;
  // A brand-new chat has an EMPTY name, not a missing one, so `??` let the
  // blank through and the drawer head rendered nameless. Same fallback the
  // rows and tabs use, so one chat reads the same everywhere.
  const label =
    scope?.kind === "canvas"
      ? scopeCanvas?.name?.trim() || "Untitled"
      : scopeSession?.name?.trim() || "Untitled chat";
  const ScopeIcon = scope?.kind === "chat" ? MessagesSquare : Frame;

  const reenter = useCallback(() => {
    if (!scope) return;
    navigate(
      scope.kind === "canvas"
        ? route(ROUTES.CANVAS.DETAIL, { canvasId: scope.id })
        : route(ROUTES.CHAT.DETAIL, { flowSessionId: scope.id }),
    );
  }, [navigate, scope]);

  // Drag the drawer's top edge — bottom is pinned, so the share is
  // (bottom − pointerY) over the rail column's height.
  // One state write per animation frame. A pointermove can fire several times
  // between frames, and each write re-renders the drawer's whole subtree —
  // the chat rows, the contents list — so writing per event does that work
  // repeatedly to produce a single painted frame.
  const pendingPct = useRef<number | null>(null);
  const pctFrame = useRef(0);
  const onResizeMove = useCallback(
    (e: React.PointerEvent) => {
      if (!resizing || !sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const host = sectionRef.current.parentElement?.getBoundingClientRect();
      if (!host) return;
      pendingPct.current = clampPct(
        ((rect.bottom - e.clientY) / host.height) * 100,
      );
      if (pctFrame.current) return;
      pctFrame.current = requestAnimationFrame(() => {
        pctFrame.current = 0;
        if (pendingPct.current !== null) setPct(pendingPct.current);
      });
    },
    [resizing],
  );
  useEffect(
    () => () => {
      if (pctFrame.current) cancelAnimationFrame(pctFrame.current);
    },
    [],
  );

  return (
    <section
      ref={sectionRef}
      aria-label="Container drawer"
      // inert (not aria-hidden): removes the hidden subtree from both the
      // a11y tree and the tab order without trapping a focused control.
      inert={present ? undefined : true}
      // Ground: the compose well (white in light, n-05 in dark) — one rung
      // brighter than the card. The section itself is the DS outer accordion:
      // it stays mounted and rides 0fr→1fr + fade on the spring, so entering
      // a container slides the whole drawer up on the rail's 300ms clock
      // (mount-and-pop can't transition). The top hairline applies only while
      // present so no stray line rides above the footer when dismissed.
      className={cn(
        "relative grid shrink-0 bg-white dark:bg-n-05 transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
        present
          ? "grid-rows-[1fr] border-t border-n-10 opacity-100"
          : "grid-rows-[0fr] opacity-0",
      )}
    >
      {/* resize handle — invisible strip on the top edge; grip pill fades in */}
      {expanded && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize drawer"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={PCT_MIN}
          aria-valuemax={PCT_MAX}
          tabIndex={0}
          data-resizing={resizing || undefined}
          onPointerDown={(e) => {
            e.preventDefault();
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              /* released pointer id — drag still tracks on the handle */
            }
            setResizing(true);
          }}
          onPointerMove={onResizeMove}
          onPointerUp={() => setResizing(false)}
          onDoubleClick={() => setPct(PCT_DEFAULT)}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setPct(clampPct(pct + 5));
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setPct(clampPct(pct - 5));
            }
          }}
          // Taller than it looks (8px) so the edge is easy to grab.
          className="absolute inset-x-0 -top-1 z-10 flex h-2 cursor-row-resize items-center justify-center outline-none hover:[&>div]:opacity-100 hover:[&>div]:bg-n-40 data-[resizing]:[&>div]:opacity-100 data-[resizing]:[&>div]:bg-n-40 focus-visible:[&>div]:opacity-100 focus-visible:[&>div]:bg-rust"
        >
          {/* Hidden at rest; the pill fades in on boundary hover, drag, or
              keyboard focus (Aug 2026 ruling — matches the DS handle idiom:
              invisible strip, hover-revealed grip). The 8px strip stays the
              grab target either way. */}
          <div className="h-1 w-8 rounded-full bg-n-30 opacity-0 transition-[opacity,background-color] duration-150 ease-out" />
        </div>
      )}

      {/* single grid child — min-h-0 + overflow-hidden let the outer
          accordion actually collapse; header + body live inside. */}
      <div className="flex min-h-0 flex-col overflow-hidden">
        {/* header — chip + name toggle; ⤢ when docked; ⋯ overflow */}
        <div className="flex min-h-12 shrink-0 items-center gap-2 px-2">
          <button
            type="button"
            onClick={() => {
              // The header gesture IS the preference: collapsing by hand
              // sticks across container entries, expanding clears it.
              manuallyCollapsedRef.current = expanded;
              setExpanded(!expanded);
            }}
            aria-expanded={expanded}
            aria-label={
              expanded ? `Collapse ${label} drawer` : `Expand ${label} drawer`
            }
            className="group flex min-w-0 items-center gap-2 rounded-md text-left outline-none focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
          >
            {/* accent chip — hover-swaps to the disclosure chevron (SwapIcon
              idiom, one size up; same box so nothing shifts) */}
            <span className="relative flex size-6 shrink-0 items-center justify-center">
              <span className="absolute inset-0 flex items-center justify-center rounded-sm bg-plum/10 text-plum transition-opacity duration-150 group-hover:opacity-0">
                <ScopeIcon className="size-3.5" />
              </span>
              <ChevronRight
                className={cn(
                  "absolute size-4 text-n-50 opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover:opacity-100",
                  expanded && "rotate-90",
                )}
              />
            </span>
            <span className="min-w-0 truncate text-base font-medium text-n-90">
              {label}
            </span>
          </button>
          {docked && (
            <button
              type="button"
              aria-label={`Open ${label}`}
              title={`Open ${label}`}
              onClick={reenter}
              className="inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-n-50 outline-none transition-colors duration-150 ease-out hover:bg-n-10 hover:text-n-80 hover:duration-100 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
            >
              <SquareArrowOutUpRight className="size-3" />
            </button>
          )}
          <div className="min-w-0 flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Drawer options"
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-n-50 outline-none transition-colors duration-150 ease-out hover:bg-n-10 hover:text-n-80 hover:duration-100 focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
              >
                <MoreHorizontal className="size-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start" sideOffset={6}>
              {docked && (
                <DropdownMenuItem onSelect={reenter}>
                  <SquareArrowOutUpRight className="size-3.5 text-n-50" />
                  Open {scope?.kind === "canvas" ? "canvas" : "chat"}
                </DropdownMenuItem>
              )}
              {scope?.kind === "canvas" && scopeCanvas && (
                <>
                  <DropdownMenuItem
                    onSelect={() =>
                      window.setTimeout(
                        () =>
                          void runRenameCanvasWorkflow({ canvas: scopeCanvas }),
                        0,
                      )
                    }
                  >
                    <PenLine className="size-3.5 text-n-50" />
                    Rename canvas
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() =>
                      window.setTimeout(
                        () =>
                          void runShareCanvasWorkflow({ canvas: scopeCanvas }),
                        0,
                      )
                    }
                  >
                    <Share2 className="size-3.5 text-n-50" />
                    Share…
                  </DropdownMenuItem>
                </>
              )}
              {scope?.kind === "chat" && scopeSession && (
                <DropdownMenuItem
                  onSelect={() =>
                    window.setTimeout(
                      () =>
                        // The rename callable takes the inbox shape; wrap the
                        // session in the same minimal item the tab bar builds.
                        void runRenameChatWorkflow({
                          item: {
                            id: scopeSession.id,
                            organization_id: scopeSession.organization_id,
                            user_id: scopeSession.user_id,
                            flow_session_id: scopeSession.id,
                            resource_type: "flow_session",
                            is_read: true,
                            is_starred: false,
                            is_archived: false,
                            has_pending_tool_confirmations: false,
                            created_at: "",
                            updated_at: "",
                            flow_session: scopeSession,
                          },
                        }),
                      0,
                    )
                  }
                >
                  <PenLine className="size-3.5 text-n-50" />
                  Rename chat
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={() => {
                  (document.activeElement as HTMLElement | null)?.blur();
                  setDismissed(true);
                }}
              >
                <PanelBottomClose className="size-3.5 text-n-50" />
                Close drawer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* body — the DS accordion: 0fr→1fr grid rows animate the dock/expand
          (height:auto can't); contents stay mounted and cached across
          docking. Column height = the drawer's share of the rail card (the
          card is a size container) minus the header rung. */}
        <div
          className={cn(
            "overflow-hidden",
            // NOT while dragging the top edge: the drag writes a new height
            // on every pointer move, and with a transition attached each move
            // restarts a 300ms animation — the drawer chases the cursor
            // instead of tracking it, re-animating dozens of times a second.
            // The open/close move is animated; the drag is 1:1.
            !resizing &&
              "transition-[height] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
            !expanded && "invisible",
          )}
          // The drawer's open height is a known share of the rail card, so
          // animate THAT rather than `grid-template-rows: 0fr→1fr`. The grid
          // form animates toward whatever the content measures, and when the
          // drawer opens because you entered a container, the content for
          // that container has not laid out yet at the moment the flip
          // happens — the row animates to nothing and then snaps once the
          // body arrives. A declared height has no such dependency, so the
          // move is identical whether the body is ready or not.
          // The second term is the ceiling: however far the drag goes, the
          // drawer stops one run-gap short of the search row instead of
          // butting into it. A fixed inset, not a percentage — the gap
          // should look the same on a laptop and a tall monitor.
          style={{
            height: expanded
              ? `min(calc(${pct}cqh - ${HEADER_PX}px), calc(100cqh - ${HEADER_PX + TOP_INSET_PX}px))`
              : 0,
          }}
        >
          {scope && bodyMounted && (
            <div className="flex h-full flex-col">
              {scope.kind === "canvas" ? (
                docked ? (
                  // Off the canvas route Liveblocks isn't connected, so the
                  // live layer tree is empty — the docked peek reads the
                  // canvas's files from the API instead.
                  <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-2">
                    <DrawerFilesGroup label="Canvas Files">
                      <FilesRailCanvasFilesList canvasId={scope.id} />
                    </DrawerFilesGroup>
                  </div>
                ) : (
                  <CanvasRailBody canvasId={scope.id} />
                )
              ) : (
                <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-2">
                  <ChatRowDropZone flowSessionId={scope.id}>
                    <DrawerFilesGroup label="Chat Files">
                      <FilesRailChatContextBody flowSessionId={scope.id} />
                    </DrawerFilesGroup>
                  </ChatRowDropZone>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
